import { FC, useMemo } from "react";
import { Grid2 as Grid, Box, Stack, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import PatientProfileTile from "@components/Home/PatientProfileTile/PatientProfileTile";
import CompanionTile from "@components/Home/CompanionTile";
import RecentConversationsTile from "@components/Home/RecentConversationsTile";
import UpcomingScheduleTile from "@components/Home/UpcomingScheduleTile";
import AlbumsTile from "@components/Home/AlbumsTile";
import CaregiverGamesPanel from "@components/Home/Games/CaregiverGamesPanel";
import DashboardWrapper from "@components/Home/DashboardWrapper";
import { useDriver } from "@hooks/useDriver";
import { useGetSessionsQuery } from "@store/sessionApi";
import { Order } from "@models/page-options";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddAlarmOutlinedIcon from "@mui/icons-material/AddAlarmOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
const QuickActionButton = styled(Button)`
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.25;
  white-space: normal;
  overflow-wrap: break-word;
  text-align: center;
  min-width: 0 !important;

  @media (max-width: 599px) {
    width: 100%;
    padding: 8px 12px;
    font-size: 0.75rem;
  }

  @media (min-width: 600px) {
    flex: 1 1 0;
  }
` as typeof Button;

const EngagementBanner: FC<{ patientId: string }> = ({ patientId }) => {
  const { t } = useTranslation();
  const { data: sessions } = useGetSessionsQuery(
    { patientId, order: Order.DESC, page: 1, pageSize: 1 },
    { skip: !patientId },
  );

  const { color, bgColor, borderColor, icon, message } = useMemo(() => {
    const last = sessions?.data?.[0];
    if (!last) return { color: "#565D6D", bgColor: "#F4F6FA", borderColor: "#E0E0E0", icon: <AccessTimeIcon sx={{ fontSize: 18 }} />, message: t("dashboard.engagementBanner.noSessions") };

    const lastDate = new Date(last.createdDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    lastDate.setHours(0, 0, 0, 0);
    const diff = Math.floor((today.getTime() - lastDate.getTime()) / 86400000);

    if (diff === 0) return {
      color: "#2E7D32", bgColor: "#F0FFF4", borderColor: "#A5D6A7",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#2E7D32" }} />,
      message: t("dashboard.engagementBanner.activeToday"),
    };
    if (diff === 1) return {
      color: "#1565C0", bgColor: "#EEF4FF", borderColor: "#90CAF9",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#1565C0" }} />,
      message: t("dashboard.engagementBanner.activeYesterday"),
    };
    if (diff <= 3) return {
      color: "#E65100", bgColor: "#FFF8F0", borderColor: "#FFCC80",
      icon: <WarningAmberIcon sx={{ fontSize: 18, color: "#E65100" }} />,
      message: t("dashboard.engagementBanner.lastActiveDaysAgo", { count: diff }),
    };
    return {
      color: "#B71C1C", bgColor: "#FFF5F5", borderColor: "#EF9A9A",
      icon: <WarningAmberIcon sx={{ fontSize: 18, color: "#B71C1C" }} />,
      message: t("dashboard.engagementBanner.lastActiveDaysAgoCheckIn", { count: diff }),
    };
  }, [sessions, t]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderRadius: 3, bgcolor: bgColor, border: `1px solid ${borderColor}`, mb: 2 }}>
      {icon}
      <Typography fontSize="0.875rem" fontWeight={500} color={color}>{message}</Typography>
    </Box>
  );
};

const QuickActionsBar: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      gap={1.5}
      sx={{ mb: 2, width: "100%" }}
    >
      <QuickActionButton
        variant="outlined"
        startIcon={<AddAlarmOutlinedIcon />}
        onClick={() => navigate("/schedule")}
      >
        {t("dashboard.quickActions.addReminder")}
      </QuickActionButton>
      <QuickActionButton
        variant="outlined"
        startIcon={<ChatOutlinedIcon />}
        onClick={() => navigate("/transcripts/")}
      >
        {t("dashboard.quickActions.transcripts")}
      </QuickActionButton>
      <QuickActionButton
        variant="outlined"
        startIcon={<PhotoLibraryOutlinedIcon />}
        onClick={() => navigate("/gallery")}
      >
        {t("dashboard.quickActions.gallery")}
      </QuickActionButton>
    </Stack>
  );
};

const Dashboard: FC = () => {
  useDriver();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );

  return (
    <DashboardWrapper>
      {selectedPatientId && <EngagementBanner patientId={selectedPatientId} />}
      <QuickActionsBar />

      <Grid container spacing={3}>
        {/* Row 1: Patient profile (1/3) + Companion hero (2/3) */}
        <Grid size={{ xs: 12, md: 4 }}>
          <PatientProfileTile />
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <CompanionTile />
        </Grid>

        {/* Row 2: Games (1/2) + Conversations (1/2) */}
        {selectedPatientId && (
          <Grid size={{ xs: 12, md: 6 }}>
            <CaregiverGamesPanel patientId={selectedPatientId} />
          </Grid>
        )}
        <Grid size={{ xs: 12, md: selectedPatientId ? 6 : 12 }}>
          <RecentConversationsTile />
        </Grid>

        {/* Row 3: Albums (1/2) + Schedule (1/2) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <AlbumsTile />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <UpcomingScheduleTile />
        </Grid>
      </Grid>
    </DashboardWrapper>
  );
};

export default Dashboard;
